import { Alert, Button, Chip, faDigits, Field, formatMoney, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { fiscalYearsApi, type FiscalYear, type PeriodLock, type YearClosePreview } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

export function FiscalYearsTab() {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [unlockTarget, setUnlockTarget] = useState<PeriodLock | null>(null);
  const [unlockReason, setUnlockReason] = useState("");
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeYearInput, setCloseYearInput] = useState("");
  const [closingDate, setClosingDate] = useState("");
  const [nextYearOpeningDate, setNextYearOpeningDate] = useState("");
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState("");

  // New year form state
  const [newYearNumber, setNewYearNumber] = useState<number>(1406);
  const [newYearTitle, setNewYearTitle] = useState("");
  const [newYearStartDate, setNewYearStartDate] = useState("2027-03-21");
  const [newYearEndDate, setNewYearEndDate] = useState("2028-03-19");

  // Queries
  const yearsQuery = useQuery({
    queryKey: ["accounting", "fiscal-years"],
    queryFn: () => fiscalYearsApi.listYears(),
  });

  const years: FiscalYear[] = yearsQuery.data ?? [];

  // Active selected year (fallback to latest open or first)
  const activeYear = useMemo(() => {
    if (selectedYear !== null) {
      const found = years.find((y) => y.year === selectedYear);
      if (found) return found;
    }
    return years.find((y) => y.status === "open") ?? years[0] ?? null;
  }, [years, selectedYear]);

  const currentYearNumber = activeYear?.year ?? null;

  const periodsQuery = useQuery({
    queryKey: ["accounting", "periods", currentYearNumber],
    queryFn: () => (currentYearNumber ? fiscalYearsApi.listPeriods(currentYearNumber) : Promise.resolve([])),
    enabled: currentYearNumber !== null,
  });

  const previewQuery = useQuery({
    queryKey: ["accounting", "close-preview", currentYearNumber],
    queryFn: () =>
      currentYearNumber ? fiscalYearsApi.getClosePreview(currentYearNumber) : Promise.resolve(null),
    enabled: currentYearNumber !== null && activeYear?.status === "open",
  });

  const periods: PeriodLock[] = periodsQuery.data ?? [];
  const preview: YearClosePreview | null = previewQuery.data ?? null;

  // Actions
  const createYearAction = useAction(
    async (body: { year: number; title: string; startDate: string; endDate: string }) => {
      await fiscalYearsApi.createYear(body);
      setShowCreateModal(false);
    },
    [["accounting", "fiscal-years"]],
  );

  const lockPeriodAction = useAction(
    async (body: { fiscalYear: number; periodNumber: number }) => {
      await fiscalYearsApi.lockPeriod(body);
    },
    [["accounting", "periods"]],
  );

  const unlockPeriodAction = useAction(
    async (body: { fiscalYear: number; periodNumber: number; reason: string }) => {
      await fiscalYearsApi.unlockPeriod(body);
      setUnlockTarget(null);
      setUnlockReason("");
    },
    [["accounting", "periods"]],
  );

  const closeYearAction = useAction(
    async (body: { fiscalYear: number; closingDate: string; nextYearOpeningDate: string }) => {
      await fiscalYearsApi.closeYear(body.fiscalYear, body);
      setShowCloseModal(false);
      setCloseYearInput("");
    },
    [
      ["accounting", "fiscal-years"],
      ["accounting", "periods"],
      ["accounting", "vouchers"],
      ["accounting", "close-preview"],
    ],
  );

  const reopenYearAction = useAction(
    async (body: { fiscalYear: number; reason: string }) => {
      await fiscalYearsApi.reopenYear(body.fiscalYear, body);
      setShowReopenModal(false);
      setReopenReason("");
    },
    [
      ["accounting", "fiscal-years"],
      ["accounting", "periods"],
      ["accounting", "vouchers"],
      ["accounting", "close-preview"],
    ],
  );

  const handleOpenCloseModal = () => {
    if (!activeYear) return;
    setClosingDate(activeYear.endDate);
    // default next day for opening
    const parts = activeYear.endDate.split("-").map(Number);
    if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
      const d = new Date(parts[0], parts[1] - 1, parts[2] + 1);
      const isoNext = `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      setNextYearOpeningDate(isoNext);
    } else {
      setNextYearOpeningDate("");
    }
    setCloseYearInput("");
    setShowCloseModal(true);
  };

  return (
    <div className="stack" style={{ gap: "var(--s5)" }}>
      {/* Fiscal Years Bar */}
      <Panel title="مدیریت سال‌های مالی و دوره‌ها">
        <div className="row wrap justify-between" style={{ alignItems: "center", gap: "var(--s3)" }}>
          <div className="row wrap" style={{ alignItems: "center", gap: "var(--s3)" }}>
            <span className="text-sm font-bold">سال مالی فعال:</span>
            {yearsQuery.isLoading ? (
              <span className="text-secondary text-sm">در حال بارگذاری...</span>
            ) : years.length === 0 ? (
              <span className="text-secondary text-sm">هیچ سال مالی ثبت نشده است</span>
            ) : (
              <select
                className="select"
                style={{ minWidth: "160px" }}
                value={activeYear?.year ?? ""}
                onChange={(e) => {
                  setSelectedYear(Number(e.target.value));
                }}
              >
                {years.map((y) => (
                  <option key={y.id} value={y.year}>
                    {y.title} ({y.status === "open" ? "باز" : "بسته"})
                  </option>
                ))}
              </select>
            )}

            {activeYear && (
              <div className="row" style={{ alignItems: "center", gap: "var(--s2)" }}>
                <Chip tone={activeYear.status === "open" ? "ok" : "coral"}>
                  {activeYear.status === "open" ? "وضعیت: باز" : "وضعیت: بسته"}
                </Chip>
                <span className="text-xs text-secondary">
                  (از {activeYear.startDate} تا {activeYear.endDate})
                </span>
              </div>
            )}
          </div>

          <Button
            variant="default"
            onClick={() => {
              if (activeYear) {
                setNewYearNumber(activeYear.year + 1);
                setNewYearTitle(`سال مالی ${String(activeYear.year + 1)}`);
              }
              setShowCreateModal(true);
            }}
          >
            + سال مالی جدید
          </Button>
        </div>
      </Panel>

      {activeYear && (
        <>
          {/* Monthly Periods Grid */}
          <Panel
            title={`قفل دوره‌های ماهانه — ${activeYear.title}`}
            actions={
              <span className="text-xs text-secondary">
                ثبت و ویرایش سند در دوره‌های قفل‌شده اکیداً متوقف می‌گردد.
              </span>
            }
          >
            <div className="stack" style={{ gap: "var(--s3)" }}>
              <ErrorLine error={lockPeriodAction.error || unlockPeriodAction.error} />

              {periodsQuery.isLoading ? (
                <div className="p-4 text-secondary">در حال دریافت وضعیت دوره‌ها...</div>
              ) : periods.length === 0 ? (
                <div className="p-4 text-secondary">دوره‌ای برای این سال مالی یافت نشد.</div>
              ) : (
                <div className="table-wrap">
                  <table className="table" dir="rtl">
                    <thead>
                      <tr>
                        <th style={{ width: "60px" }}>دوره</th>
                        <th style={{ width: "120px" }}>نام ماه</th>
                        <th>بازه تاریخی (میلادی)</th>
                        <th style={{ width: "120px" }}>وضعیت</th>
                        <th>توضیحات قفل / بازگشایی</th>
                        <th style={{ width: "140px", textAlign: "center" }}>عملیات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periods.map((p) => (
                        <tr key={p.id}>
                          <td className="font-mono">{faDigits(p.periodNumber)}</td>
                          <td className="font-bold">{p.title}</td>
                          <td className="font-mono text-xs text-secondary">
                            {p.fromDate} الی {p.toDate}
                          </td>
                          <td>
                            <Chip tone={p.isLocked ? "coral" : "ok"}>{p.isLocked ? "قفل شده" : "باز"}</Chip>
                          </td>
                          <td className="text-xs text-secondary">
                            {p.isLocked && p.lockedAt && (
                              <span>قفل در {new Date(p.lockedAt).toLocaleDateString("fa-IR")}</span>
                            )}
                            {!p.isLocked && p.unlockReason && <span>دلیل بازگشایی: {p.unlockReason}</span>}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {activeYear.status === "closed" ? (
                              <span className="text-xs text-secondary">سال بسته است</span>
                            ) : p.isLocked ? (
                              <Button
                                variant="default"
                                onClick={() => {
                                  setUnlockTarget(p);
                                  setUnlockReason("");
                                }}
                              >
                                بازگشایی...
                              </Button>
                            ) : (
                              <Button
                                variant="urgent"
                                disabled={lockPeriodAction.busy}
                                onClick={() => {
                                  lockPeriodAction.run({
                                    fiscalYear: p.fiscalYear,
                                    periodNumber: p.periodNumber,
                                  });
                                }}
                              >
                                قفل دوره
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Panel>

          {/* Year Close / Reopen Section */}
          <Panel title={`بستن و انتقال مانده‌های سال مالی — ${activeYear.title}`}>
            <div className="stack" style={{ gap: "var(--s4)" }}>
              {activeYear.status === "closed" ? (
                <div className="stack" style={{ gap: "var(--s4)" }}>
                  <Alert
                    level="important"
                    icon="lock"
                    title={`این سال مالی در تاریخ ${
                      activeYear.closedAt
                        ? new Date(activeYear.closedAt).toLocaleDateString("fa-IR")
                        : "نامشخص"
                    } بسته شده است.`}
                  >
                    کلیه حساب‌های موقت صفر گردیده و اسناد اختتامیه و افتتاحیه صادر شده‌اند.
                  </Alert>

                  <div className="row wrap" style={{ gap: "var(--s3)" }}>
                    <div className="card" style={{ flex: 1, minWidth: "220px" }}>
                      <div className="stack" style={{ gap: "var(--s1)" }}>
                        <span className="text-xs text-secondary">سند بستن حساب‌های موقت</span>
                        <span className="font-mono text-sm">{activeYear.closingVoucherId ?? "—"}</span>
                      </div>
                    </div>
                    <div className="card" style={{ flex: 1, minWidth: "220px" }}>
                      <div className="stack" style={{ gap: "var(--s1)" }}>
                        <span className="text-xs text-secondary">سند اختتامیه ترازنامه</span>
                        <span className="font-mono text-sm">{activeYear.balanceClosingVoucherId ?? "—"}</span>
                      </div>
                    </div>
                    <div className="card" style={{ flex: 1, minWidth: "220px" }}>
                      <div className="stack" style={{ gap: "var(--s1)" }}>
                        <span className="text-xs text-secondary">سند افتتاحیه سال بعد</span>
                        <span className="font-mono text-sm">{activeYear.nextOpeningVoucherId ?? "—"}</span>
                      </div>
                    </div>
                  </div>

                  <div
                    className="row justify-between"
                    style={{ alignItems: "center", marginTop: "var(--s2)" }}
                  >
                    <div className="text-xs text-secondary">
                      بازگشایی سال مالی صرفاً با تایید مدیر ارشد و به همراه صدور اسناد معکوس انجام می‌شود.
                    </div>
                    <Button
                      variant="urgent"
                      onClick={() => {
                        setReopenReason("");
                        setShowReopenModal(true);
                      }}
                    >
                      بازگشایی سال مالی...
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="stack" style={{ gap: "var(--s4)" }}>
                  <p className="text-secondary text-sm">
                    پیش از بستن سال مالی، کلیه اسناد پیش‌نویس باید تعیین تکلیف و تراز آزمایشی باید کاملاً
                    متوازن باشد. بستن سال مالی حساب‌های درآمد و هزینه را بسته، سود/زیان را به حساب سود و زیان
                    سال منتقل کرده و سند اختتامیه و افتتاحیه سال آینده را به صورت قطعی صادر می‌کند.
                  </p>

                  <ErrorLine error={closeYearAction.error} />

                  {previewQuery.isLoading ? (
                    <div className="p-4 text-secondary">در حال محاسبه تراز و پیش‌نمایش بستن سال مالی...</div>
                  ) : preview ? (
                    <div className="stack" style={{ gap: "var(--s4)" }}>
                      {/* Financial KPI Cards */}
                      <div className="row wrap" style={{ gap: "var(--s3)" }}>
                        <div className="card" style={{ flex: 1, minWidth: "200px" }}>
                          <div className="stack" style={{ gap: "var(--s1)" }}>
                            <span className="text-xs text-secondary">مجموع درآمدهای سال</span>
                            <span className="text-lg font-bold" style={{ color: "var(--teal-ink, #0d9488)" }}>
                              {formatMoney(preview.totalRevenueRial)}
                            </span>
                          </div>
                        </div>
                        <div className="card" style={{ flex: 1, minWidth: "200px" }}>
                          <div className="stack" style={{ gap: "var(--s1)" }}>
                            <span className="text-xs text-secondary">مجموع هزینه‌ها و بهای تمام‌شده</span>
                            <span
                              className="text-lg font-bold"
                              style={{ color: "var(--coral-ink, #e11d48)" }}
                            >
                              {formatMoney(preview.totalExpenseRial)}
                            </span>
                          </div>
                        </div>
                        <div className="card" style={{ flex: 1, minWidth: "200px" }}>
                          <div className="stack" style={{ gap: "var(--s1)" }}>
                            <span className="text-xs text-secondary">سود / زیان خالص سال مالی</span>
                            <span
                              className="text-lg font-bold"
                              style={{
                                color:
                                  preview.netProfitLossRial >= 0
                                    ? "var(--teal-ink, #0d9488)"
                                    : "var(--coral-ink, #e11d48)",
                              }}
                            >
                              {preview.netProfitLossRial >= 0 ? "سود: " : "زیان: "}
                              {formatMoney(Math.abs(preview.netProfitLossRial))}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Result Account Assigned */}
                      <div className="text-sm">
                        حساب انتقال نتیجه سال مالی:{" "}
                        <span className="font-bold">
                          {preview.resultAccount.code} — {preview.resultAccount.name}
                        </span>
                      </div>

                      {/* Blocking Reasons if any */}
                      {!preview.canClose && (
                        <Alert level="urgent" icon="alert" title="موارد مانع بستن سال مالی">
                          <ul style={{ paddingRight: "var(--s4)", margin: 0 }}>
                            {preview.blockingReasons.map((reason, idx) => (
                              <li key={idx}>{reason}</li>
                            ))}
                          </ul>
                        </Alert>
                      )}

                      {/* Close Year Trigger */}
                      <div className="row justify-end" style={{ marginTop: "var(--s3)" }}>
                        <Button
                          variant="primary"
                          disabled={!preview.canClose || closeYearAction.busy}
                          onClick={handleOpenCloseModal}
                        >
                          بستن سال مالی {activeYear.title}...
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </Panel>
        </>
      )}

      {/* Modal: New Fiscal Year */}
      {showCreateModal && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: "480px", width: "100%" }}>
            <Panel title="تعریف سال مالی جدید">
              <div className="stack" style={{ gap: "var(--s4)" }}>
                <ErrorLine error={createYearAction.error} />

                <Field id="new-fy-year" label="شماره سال شمسی">
                  <input
                    id="new-fy-year"
                    type="number"
                    className="input font-mono"
                    value={newYearNumber}
                    onChange={(e) => {
                      setNewYearNumber(Number(e.target.value));
                    }}
                  />
                </Field>

                <Field id="new-fy-title" label="عنوان سال مالی">
                  <input
                    id="new-fy-title"
                    type="text"
                    className="input"
                    value={newYearTitle}
                    onChange={(e) => {
                      setNewYearTitle(e.target.value);
                    }}
                  />
                </Field>

                <div className="row wrap" style={{ gap: "var(--s3)" }}>
                  <Field id="new-fy-start" label="تاریخ شروع (میلادی)">
                    <input
                      id="new-fy-start"
                      type="date"
                      className="input font-mono"
                      value={newYearStartDate}
                      onChange={(e) => {
                        setNewYearStartDate(e.target.value);
                      }}
                    />
                  </Field>
                  <Field id="new-fy-end" label="تاریخ پایان (میلادی)">
                    <input
                      id="new-fy-end"
                      type="date"
                      className="input font-mono"
                      value={newYearEndDate}
                      onChange={(e) => {
                        setNewYearEndDate(e.target.value);
                      }}
                    />
                  </Field>
                </div>

                <div className="row justify-end" style={{ gap: "var(--s2)", marginTop: "var(--s2)" }}>
                  <Button
                    variant="default"
                    onClick={() => {
                      setShowCreateModal(false);
                    }}
                  >
                    انصراف
                  </Button>
                  <Button
                    variant="primary"
                    disabled={createYearAction.busy || !newYearTitle.trim()}
                    onClick={() => {
                      createYearAction.run({
                        year: newYearNumber,
                        title: newYearTitle.trim(),
                        startDate: newYearStartDate,
                        endDate: newYearEndDate,
                      });
                    }}
                  >
                    ثبت سال مالی
                  </Button>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* Modal: Unlock Period */}
      {unlockTarget && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: "480px", width: "100%" }}>
            <Panel
              title={`بازگشایی دوره ${unlockTarget.title} (دوره ${faDigits(unlockTarget.periodNumber)})`}
            >
              <div className="stack" style={{ gap: "var(--s4)" }}>
                <p className="text-secondary text-sm">
                  بازگشایی دوره به کاربران اجازه می‌دهد در این ماه اسناد جدید ثبت یا ویرایش کنند. درج دلیل
                  بازگشایی در لاگ حسابرسی الزامی است.
                </p>

                <ErrorLine error={unlockPeriodAction.error} />

                <Field id="unlock-reason" label="دلیل بازگشایی دوره (الزامی)">
                  <textarea
                    id="unlock-reason"
                    className="input"
                    rows={3}
                    placeholder="مثال: اصلاح ثبت سند مغایرت بانکی با مجوز سرپرست مالی..."
                    value={unlockReason}
                    onChange={(e) => {
                      setUnlockReason(e.target.value);
                    }}
                  />
                </Field>

                <div className="row justify-end" style={{ gap: "var(--s2)" }}>
                  <Button
                    variant="default"
                    onClick={() => {
                      setUnlockTarget(null);
                    }}
                  >
                    انصراف
                  </Button>
                  <Button
                    variant="primary"
                    disabled={unlockPeriodAction.busy || !unlockReason.trim()}
                    onClick={() => {
                      unlockPeriodAction.run({
                        fiscalYear: unlockTarget.fiscalYear,
                        periodNumber: unlockTarget.periodNumber,
                        reason: unlockReason.trim(),
                      });
                    }}
                  >
                    تایید و بازگشایی دوره
                  </Button>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* Modal: Strong Close Year Confirmation */}
      {showCloseModal && activeYear && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: "520px", width: "100%" }}>
            <Panel title={`تایید نهایی بستن سال مالی ${activeYear.title}`}>
              <div className="stack" style={{ gap: "var(--s4)" }}>
                <Alert level="urgent" icon="alert" title="عملیات بستن سال مالی قطعی و حساس است">
                  <div className="stack" style={{ gap: "var(--s1)" }}>
                    <span>۱. کلیه حساب‌های درآمد و هزینه (موقت) صفر خواهند شد.</span>
                    <span>۲. سند اختتامیه در پایان سال صادر شده و تمام دوره‌ها قفل می‌شوند.</span>
                    <span>۳. سند افتتاحیه به سال مالی بعد انتقال خواهد یافت.</span>
                  </div>
                </Alert>

                <ErrorLine error={closeYearAction.error} />

                <Field id="close-fy-date" label="تاریخ سند اختتامیه">
                  <input
                    id="close-fy-date"
                    type="date"
                    className="input font-mono"
                    value={closingDate}
                    onChange={(e) => {
                      setClosingDate(e.target.value);
                    }}
                  />
                </Field>

                <Field id="close-fy-next-date" label="تاریخ سند افتتاحیه سال بعد">
                  <input
                    id="close-fy-next-date"
                    type="date"
                    className="input font-mono"
                    value={nextYearOpeningDate}
                    onChange={(e) => {
                      setNextYearOpeningDate(e.target.value);
                    }}
                  />
                </Field>

                <Field
                  id="close-fy-confirm"
                  label={`برای تایید، عدد «${faDigits(activeYear.year)}» را تایپ فرمایید:`}
                >
                  <input
                    id="close-fy-confirm"
                    type="text"
                    dir="ltr"
                    className="input font-mono text-center font-bold"
                    placeholder={String(activeYear.year)}
                    value={closeYearInput}
                    onChange={(e) => {
                      setCloseYearInput(e.target.value);
                    }}
                  />
                </Field>

                <div className="row justify-end" style={{ gap: "var(--s2)", marginTop: "var(--s2)" }}>
                  <Button
                    variant="default"
                    onClick={() => {
                      setShowCloseModal(false);
                    }}
                  >
                    انصراف
                  </Button>
                  <Button
                    variant="urgent"
                    disabled={
                      closeYearAction.busy ||
                      closeYearInput.trim() !== String(activeYear.year) ||
                      !closingDate ||
                      !nextYearOpeningDate
                    }
                    onClick={() => {
                      closeYearAction.run({
                        fiscalYear: activeYear.year,
                        closingDate,
                        nextYearOpeningDate,
                      });
                    }}
                  >
                    بستن قطعی سال مالی
                  </Button>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* Modal: Reopen Fiscal Year */}
      {showReopenModal && activeYear && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: "500px", width: "100%" }}>
            <Panel title={`بازگشایی سال مالی ${activeYear.title}`}>
              <div className="stack" style={{ gap: "var(--s4)" }}>
                <Alert level="important" icon="shield" title="مجوز مدیر ارشد الزامی است">
                  با بازگشایی، اسناد اختتامیه و افتتاحیه معکوس شده و دوره‌ها باز می‌گردند. هیچ رکوردی از
                  پایگاه داده حذف نمی‌شود.
                </Alert>

                <ErrorLine error={reopenYearAction.error} />

                <Field id="reopen-fy-reason" label="دلیل و مجوز بازگشایی سال مالی (الزامی)">
                  <textarea
                    id="reopen-fy-reason"
                    className="input"
                    rows={3}
                    placeholder="مثال: مصوبه هیئت مدیره جهت اصلاح اسناد حسابداری سال گذشته..."
                    value={reopenReason}
                    onChange={(e) => {
                      setReopenReason(e.target.value);
                    }}
                  />
                </Field>

                <div className="row justify-end" style={{ gap: "var(--s2)" }}>
                  <Button
                    variant="default"
                    onClick={() => {
                      setShowReopenModal(false);
                    }}
                  >
                    انصراف
                  </Button>
                  <Button
                    variant="urgent"
                    disabled={reopenYearAction.busy || !reopenReason.trim()}
                    onClick={() => {
                      reopenYearAction.run({
                        fiscalYear: activeYear.year,
                        reason: reopenReason.trim(),
                      });
                    }}
                  >
                    تایید بازگشایی سال مالی
                  </Button>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
